import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Rocket, CheckCircle } from 'lucide-react';

const Signup = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signup(email, password, name);
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#050508] flex flex-col items-center justify-center p-6">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a15] via-[#050508] to-[#050508] opacity-50" />
        
        <div className="relative z-10 w-full max-w-sm text-center">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
          <h1 className="text-white text-2xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Account Created!
          </h1>
          <p className="text-white/60 mb-8">
            Your account is pending approval. You'll be able to log in once an administrator approves your request.
          </p>
          <Link
            to="/login"
            className="btn-secondary inline-block px-8 py-3"
          >
            Return to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050508] flex flex-col items-center justify-center p-6">
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a15] via-[#050508] to-[#050508] opacity-50" />
      
      <div className="relative z-10 w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 mx-auto mb-6 rounded-full gradient-primary flex items-center justify-center glow-soft">
            <Rocket className="w-10 h-10 text-black" strokeWidth={1.5} />
          </div>
          <h1 className="text-gradient text-2xl font-bold mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Callen's Mission
          </h1>
          <p className="text-white/50 text-sm">Create your account</p>
        </div>

        {/* Signup Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-white/60 text-sm mb-2">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input-modern w-full"
              placeholder="Enter your name"
              required
              data-testid="signup-name"
            />
          </div>

          <div>
            <label className="block text-white/60 text-sm mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-modern w-full"
              placeholder="Enter your email"
              required
              data-testid="signup-email"
            />
          </div>

          <div>
            <label className="block text-white/60 text-sm mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-modern w-full"
              placeholder="Create a password"
              required
              minLength={6}
              data-testid="signup-password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="btn-primary w-full py-3 disabled:opacity-50"
            data-testid="signup-submit"
          >
            {loading ? 'Creating account...' : 'Sign Up'}
          </button>
        </form>

        <p className="text-center text-white/40 text-sm mt-6">
          Already have an account?{' '}
          <Link to="/login" className="text-[#00f3ff] hover:underline">
            Sign in
          </Link>
        </p>

        <p className="text-center text-white/30 text-xs mt-8">
          New accounts require admin approval before access is granted.
        </p>
      </div>
    </div>
  );
};

export default Signup;
